import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faRotateRight,
  faMagnifyingGlass,
  faSatelliteDish,
  faCircleNodes,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import useFeedSocket from "../../../services/useFeedSocket";

/* ---- feed helpers (Calchas RFI schema, with safe fallbacks) ------------- */

function keyOf(fair) {
  if (!fair || typeof fair !== "object") return String(fair);
  return (
    fair.key ??
    fair.book_key ??
    fair.market_key ??
    fair.id ??
    fair.theo_id ??
    JSON.stringify(fair)
  );
}

function prettyLabel(s) {
  return String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const FAMILY_LABELS = {
  mlb_rfi: "MLB · RFI",
};

function familyLabel(fair) {
  if (!fair?.family) return "Feed";
  return FAMILY_LABELS[fair.family] || prettyLabel(fair.family);
}

function matchupOf(fair) {
  const e = fair?.event;
  if (e?.away && e?.home) return `${e.away} @ ${e.home}`;
  if (fair?.away && fair?.home) return `${fair.away} @ ${fair.home}`;
  return prettyLabel(fair?.family || "Fair");
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function periodLabel(period) {
  if (!period) return "Game";
  if (period.type === "inning" && period.number != null) return `${ordinal(period.number)} Inning`;
  if (period.type === "game") return "Full Game";
  if (period.number != null) return `${prettyLabel(period.type || "Period")} ${period.number}`;
  return prettyLabel(period.type || "Game");
}

function marketLabel(fair) {
  const m = fair?.market;
  if (!m) return "";
  const parts = [periodLabel(m.period)];
  if (m.market_type) parts.push(prettyLabel(m.market_type));
  const side = [m.side ? prettyLabel(m.side) : null, m.line != null ? m.line : null]
    .filter((x) => x !== null && x !== undefined)
    .join(" ");
  if (m.team) parts.push(m.team);
  return side ? `${parts.join(" · ")} · ${side}` : parts.join(" · ");
}

function fmtAmerican(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  return v > 0 ? `+${v}` : `${v}`;
}

function fmtProb(p) {
  if (p === null || p === undefined || Number.isNaN(Number(p))) return "—";
  return `${(Number(p) * 100).toFixed(1)}%`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function agoText(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  if (mins < 60) return `${mins}m ${rem}s ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

function gamePkOf(fair) {
  const id = fair?.event?.authority_game_id;
  if (typeof id === "string" && id.includes(":")) return id.split(":").pop();
  return null;
}

function scheduleStatus(game) {
  const state = (game?.state || "").toLowerCase();
  if (state === "final") return { label: "Final", cls: "bg-slate-100 text-slate-500" };
  if (state === "live") return { label: "In progress", cls: "bg-emerald-100 text-emerald-700" };
  return { label: "Pre-game", cls: "bg-amber-100 text-amber-700" };
}

/* ---- presentational pieces --------------------------------------------- */

function StatusPill({ status }) {
  const map = {
    connected: { label: "Connected", cls: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
    connecting: { label: "Connecting…", cls: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
    disconnected: { label: "Disconnected", cls: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  };
  const s = map[status] || map.disconnected;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot} ${status !== "disconnected" ? "animate-pulse" : ""}`} />
      {s.label}
    </span>
  );
}

function StatCard({ icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-slate-400">
        <FontAwesomeIcon icon={icon} className="text-sm" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
    </div>
  );
}

function BoardCard({ game, fair, lastFair, flash }) {
  const isFinal = (game?.state || "").toLowerCase() === "final";
  const live = !!fair;
  const stale = !live && !!lastFair && !isFinal;
  const shown = live ? fair : stale ? lastFair : null;

  const matchup = game
    ? `${game.awayFull || game.away} @ ${game.homeFull || game.home}`
    : matchupOf(shown);
  const startText = fmtTime(game?.startUtc || shown?.event?.start_time_utc);

  // No live or last-known odds → plain status card.
  if (!shown) {
    const st = scheduleStatus(game);
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            MLB · RFI
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
            <FontAwesomeIcon icon={faClock} className="text-[10px]" />
            {startText}
          </span>
        </div>

        <h3 className="mt-2 text-lg font-semibold leading-tight text-slate-500">{matchup}</h3>

        <div className="mt-3 flex items-center justify-between gap-2">
          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${st.cls}`}>
            {st.label}
          </span>
          <span className="text-[11px] text-slate-400">
            {isFinal ? "1st inning settled" : "awaiting model / lineups"}
          </span>
        </div>

        <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" />
      </div>
    );
  }

  const market = marketLabel(shown);
  const prob = Number(shown?.fair_prob);
  const probPct = Number.isFinite(prob) ? Math.min(100, Math.max(0, prob * 100)) : 0;
  const freshness = agoText(shown?.as_of);

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm transition-all duration-500 ${
        live && flash
          ? "feed-flash border-primary bg-white"
          : stale
          ? "border-slate-200 bg-slate-50/60"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {familyLabel(shown)}
          </span>
          {stale && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              Stale
            </span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
          <FontAwesomeIcon icon={faClock} className="text-[10px]" />
          {startText}
        </span>
      </div>

      <h3 className={`mt-2 text-lg font-semibold leading-tight ${stale ? "text-slate-600" : "text-slate-900"}`}>
        {matchup}
      </h3>
      {market && <p className="mt-0.5 text-xs font-medium text-slate-500">{market}</p>}

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Fair Probability</div>
          <div className={`font-mono text-2xl font-bold leading-none ${stale ? "text-slate-500" : "text-slate-900"}`}>
            {fmtProb(prob)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Fair Line</div>
          <div className={`font-mono text-2xl font-bold leading-none ${stale ? "text-slate-500" : "text-primary"}`}>
            {fmtAmerican(shown?.fair_american)}
          </div>
        </div>
      </div>

      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all duration-500 ${stale ? "bg-slate-300" : "bg-primary"}`}
          style={{ width: `${probPct}%` }}
        />
      </div>

      {freshness && (
        <div className="mt-2 text-right text-[11px] text-slate-400">
          {stale ? `last priced ${freshness} · line pulled` : `updated ${freshness}`}
        </div>
      )}
    </div>
  );
}

/* ---- page -------------------------------------------------------------- */

export default function LiveFeed() {
  const { status, upstream, fairs, lastFairs, schedule, changedKeys, lastUpdate, reconnect } = useFeedSocket();
  const [query, setQuery] = useState("");

  // Re-render once a second so the "updated Xs ago" labels keep counting up,
  // making it visibly live even when values haven't moved.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Merge the full schedule with live fairs so every game on the board shows,
  // even those the model isn't pricing yet.
  const board = useMemo(() => {
    const fairByPk = new Map();
    for (const f of fairs) {
      const pk = gamePkOf(f);
      if (pk) fairByPk.set(pk, f);
    }
    const lastByPk = new Map();
    for (const f of lastFairs || []) {
      const pk = f?._pk || gamePkOf(f);
      if (pk) lastByPk.set(pk, f);
    }

    let rows;
    if (Array.isArray(schedule) && schedule.length) {
      rows = schedule.map((game) => ({
        game,
        fair: fairByPk.get(game.gamePk) || null,
        lastFair: lastByPk.get(game.gamePk) || null,
        pk: game.gamePk,
      }));
      // Include any priced fair that isn't on the schedule (rare edge case).
      for (const f of fairs) {
        const pk = gamePkOf(f);
        if (pk && !schedule.some((g) => g.gamePk === pk)) {
          rows.push({ game: null, fair: f, lastFair: null, pk: pk || keyOf(f) });
        }
      }
    } else {
      // Fallback: no schedule loaded yet — show priced fairs only.
      rows = fairs.map((f) => ({
        game: null,
        fair: f,
        lastFair: null,
        pk: gamePkOf(f) || keyOf(f),
      }));
    }

    rows.sort((a, b) => {
      const ta = new Date(a.game?.startUtc || a.fair?.event?.start_time_utc || 0).getTime();
      const tb = new Date(b.game?.startUtc || b.fair?.event?.start_time_utc || 0).getTime();
      return ta - tb;
    });
    return rows;
  }, [fairs, lastFairs, schedule]);

  const filtered = useMemo(() => {
    if (!query.trim()) return board;
    const q = query.toLowerCase();
    return board.filter((row) => {
      const hay = `${row.game?.away || ""} ${row.game?.home || ""} ${row.game?.awayFull || ""} ${row.game?.homeFull || ""} ${JSON.stringify(
        row.fair || row.lastFair || {}
      )}`.toLowerCase();
      return hay.includes(q);
    });
  }, [board, query]);

  const pricedCount = fairs.length;
  const upstreamConnected = upstream?.connected;
  const lastUpdateText = lastUpdate
    ? new Date(lastUpdate).toLocaleTimeString()
    : "—";

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Live</p>
          <div className="flex items-center gap-3">
            <h1>Live Feed</h1>
            <StatusPill status={status} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reconnect} className="btn-dark">
            <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
            Reconnect
          </button>
        </div>
      </div>

      <hr />

      <div className="my-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          icon={faCircleNodes}
          label="Games On Board"
          value={board.length}
          hint={`${pricedCount} priced${query ? ` · ${filtered.length} shown` : ""}`}
        />
        <StatCard
          icon={faSatelliteDish}
          label="Upstream Feed"
          value={upstreamConnected ? "Live" : status === "connected" ? "Waiting" : "Down"}
          hint={upstream?.stopped ? "access revoked" : upstream?.lastError || "Calchas v1"}
        />
        <StatCard icon={faBolt} label="Last Update" value={lastUpdateText} hint="local time" />
        <StatCard
          icon={faCircleNodes}
          label="Sync"
          value={upstream?.epoch != null ? String(upstream.epoch).slice(0, 8) : "—"}
          hint={upstream?.seq != null ? `epoch · seq ${upstream.seq}` : "sync state"}
        />
      </div>

      <div className="relative my-4 max-w-sm">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter games…"
          className="form-control pl-9"
        />
      </div>

      {/* Body states */}
      {status !== "connected" ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <FontAwesomeIcon icon={faSatelliteDish} className="text-3xl text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-600">
            {status === "connecting" ? "Connecting to the live feed…" : "Disconnected from the live feed."}
          </p>
          {status === "disconnected" && (
            <button onClick={reconnect} className="btn-primary mt-4">
              Try again
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
          <FontAwesomeIcon icon={faBolt} className="text-3xl text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-600">
            {board.length === 0
              ? "No games scheduled right now."
              : "No games match your filter."}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Prices populate per game once lineups post (≈1–2h before first pitch).
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((row) => {
            const flashKey = row.fair ? keyOf(row.fair) : null;
            return (
              <BoardCard
                key={row.pk}
                game={row.game}
                fair={row.fair}
                lastFair={row.lastFair}
                flash={flashKey ? changedKeys.has(flashKey) : false}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
